import * as moment from "moment";
import * as _ from "lodash";
import {
  FeeCalculationType,
  FeeCalculationStatus,
} from "@common/enums/FeeCalculation";
import { extractErrorMessage } from "@common/utils/errorUtils";
import { FeesRepo } from "@services/fees.repo";

class TempRunController {
  // Shared state from parent (bindings)
  public fromDate: Date;
  public toDate: Date;
  public displayMCB: boolean;
  public threshold: number;
  public midRateDate: Date;
  public midRates: Array<any>;
  public currentDate: Date;
  public lastTwelveMonths: Date;
  public reloadTempParameters: boolean;
  public onSaveComplete: () => void;

  // Local state
  public selectedCurrency = "MUR";
  public clientFeeParameters: Array<any> = [];
  public selectAll: boolean = false;
  public isLoadingParameters: boolean = false;
  public isSavingParameters: boolean = false;
  public isRecalculating: boolean = false;
  public isGeneratingExcel: boolean = false;

  get isAnyLoading(): boolean {
    return this.isLoadingParameters || this.isSavingParameters || this.isRecalculating || this.isGeneratingExcel;
  }
  public hasSearched: boolean = false;
  public searchText: string = "";

  /** Virtual filter code for portfolios with no issue codes (not a backend issue code). */
  public static readonly NO_ISSUES_CODE = "NO_ISSUES";

  // FE-only filter: show portfolios that have any selected issue code or "No Issues"; configurable selection
  public includedIssueCodes: string[] = [];
  public issueCodeOptions: Array<{ code: string; label: string }> = [
    { code: "FUNDS", label: "FUNDS - Insufficient funds" },
    { code: "DELTA", label: "DELTA - Fee value delta exceeds threshold" },
    { code: "NO_DEFAULT_CASH_ACCOUNT", label: "NO_DEFAULT_CASH_ACCOUNT - No default cash account (debit link)" },
    { code: "MISSING_ACCOUNT_REFERENCE", label: "MISSING_ACCOUNT_REFERENCE - No active linked external account" },
    { code: "MISSING_EXCHANGE_RATE", label: "MISSING_EXCHANGE_RATE - Missing exchange rate" },
    { code: "NO_CASH_BALANCE", label: "NO_CASH_BALANCE - Default (linked) account has no balance" },
    { code: "NOT_CALCULATED", label: "NOT_CALCULATED - Fee parameters not yet calculated" },
    { code: TempRunController.NO_ISSUES_CODE, label: "No Issues" },
  ];

  // stats
  public totalNav: number = 0;
  public totalFeeValue: number = 0;
  public totalPortfolios: number = 0;

  /** @ngInject */
  constructor(
    private notifyjs,
    private feesRepo: FeesRepo,
    private $window: angular.IWindowService,
    private $scope: angular.IScope
  ) {}

  $onInit() {
    // Default: no issue codes selected = show only portfolios with no issues (none with issues shown)
    // Initialize mid rates if midRateDate is set
    if (this.midRateDate) {
      this.getMidRateForMonth();
    }
    // Keep Select All and client checkboxes in sync with the visible (filtered) list: when filter/search changes
    const self = this;
    this.$scope.$watch(
      () => [self.includedIssueCodes, self.searchText],
      () => {
        self.selectAll = self.allVisiblePortfoliosSelected();
        (self.clientFeeParameters || []).forEach((client) => {
          if (client) {
            const visibleTotal = self.getClientVisibleTotal(client);
            client.isSelected = visibleTotal > 0 && self.getClientVisibleSelectedCount(client) === visibleTotal;
          }
        });
      },
      true
    );
  }

  $onChanges(changes: any) {
    if (changes.reloadTempParameters && !changes.reloadTempParameters.isFirstChange()) {
      this.getNewInstructionsParameters();
    }
    // Fetch mid rates when midRateDate changes
    if (changes.midRateDate && this.midRateDate) {
      this.getMidRateForMonth();
    }
  }

  refreshData = () => {
    this.getNewInstructionsParameters();
  };

  getNewInstructionsParameters = () => {
    // validate if the date range is valid
    if (!this.fromDate || !this.toDate) {
      return;
    }

    if (this.fromDate > this.toDate) {
      this.notifyjs("From date cannot be greater than to date");
      return;
    }
    
    let feeCalculationType = this.displayMCB
      ? FeeCalculationType.MCB
      : FeeCalculationType.EDR;
    this.isLoadingParameters = true;
    this.hasSearched = true;
    let fromDate = moment(this.fromDate).endOf("month").format("YYYY-MM-DD");
    let toDate = moment(this.toDate).endOf("month").format("YYYY-MM-DD");
    this.feesRepo
      .getTempRunFees(
        fromDate,
        toDate,
        FeeCalculationStatus.NEW,
        this.selectedCurrency,
        feeCalculationType,
        this.threshold
      )
      .then((res) => {
        this.selectAll = false;
        this.clientFeeParameters = res.data as Array<any>;
        this.clientFeeParameters = this.clientFeeParameters.filter(
          (client) => client
        );

        // Only keep issue codes in the filter that have at least one matching portfolio; disable the rest in the dropdown
        this.syncIncludedIssueCodesToAvailable();
        // Default selection only when user hasn't chosen any filter yet (e.g. first load); otherwise preserve search and filter on reload
        if (!this.includedIssueCodes || this.includedIssueCodes.length === 0) {
          this.setDefaultIssueCodeSelection();
        }

        // Backend sends defaultCashAccountId and defaultCurrency; dropdown binds by id so it matches natively.
        // Portfolios with no default cash account cannot be selected — keep them unselected.
        this.clientFeeParameters.forEach((client) => {
          if (client) {
            client.isSelected = this.selectAll;
            client.portfolioFee.forEach((fee) => {
              fee.portfolios.forEach((portfolio) => {
                if (portfolio) {
                  portfolio.isSelected = this.portfolioIsSelectable(portfolio) ? this.selectAll : false;
                  portfolio.defaultCurrency = portfolio.defaultCurrency != null ? portfolio.defaultCurrency : null;
                  portfolio.selectedCashAccountId = portfolio.defaultCashAccountId != null ? portfolio.defaultCashAccountId : null;
                  portfolio.selectedCashAccount = this.getSelectedCashAccount(portfolio);
                }
              });
            });
          }
        });
      })
      .catch((err) => {
        this.notifyjs(extractErrorMessage(err, "Failed to retrieve fee values"));
      })
      .finally(() => {
        this.setTotalNav();
        this.setTotalFeeValue();
        this.setTotalPortfolios();
        this.isLoadingParameters = false;
      });
  };

  /** Select/deselect only portfolios that are visible and selectable (have default cash account). */
  selectAllClients = () => {
    const filterFn = this.getSearchAndIssueFilter();
    this.clientFeeParameters.forEach((client) => {
      if (!client || !filterFn(client)) return;
      client.portfolioFee.forEach((fee) => {
        if (!fee.portfolios) return;
        fee.portfolios.forEach((portfolio) => {
          if (portfolio && this.portfolioMatchesIncludedIssues(portfolio) && this.portfolioIsSelectable(portfolio)) {
            portfolio.isSelected = this.selectAll;
          }
        });
      });
      client.isSelected = this.getClientVisibleSelectedCount(client) === this.getClientVisibleTotal(client) && this.getClientVisibleTotal(client) > 0;
    });
  };

  /** True if every visible selectable portfolio (across filtered clients) is selected. */
  allVisiblePortfoliosSelected = () => {
    const filterFn = this.getSearchAndIssueFilter();
    const filteredClients = (this.clientFeeParameters || []).filter((c) => c && filterFn(c));
    let totalVisible = 0;
    let totalVisibleSelected = 0;
    filteredClients.forEach((client) => {
      client.portfolioFee.forEach((pf) => {
        (pf.portfolios || []).forEach((p) => {
          if (p && this.portfolioMatchesIncludedIssues(p) && this.portfolioIsSelectable(p)) {
            totalVisible++;
            if (p.isSelected) totalVisibleSelected++;
          }
        });
      });
    });
    return totalVisible > 0 && totalVisible === totalVisibleSelected;
  };

  /** Total number of portfolios for this client (all fee types), regardless of issue filter. */
  getClientTotalPortfolioCount = (client) => {
    if (!client || !client.portfolioFee) return 0;
    return client.portfolioFee.reduce(
      (acc, pf) => acc + (pf.portfolios || []).filter((p) => p).length,
      0
    );
  };

  /** Number of portfolios that are selected for this client (all, regardless of filter). */
  getClientSelectedCount = (client) => {
    if (!client || !client.portfolioFee) return 0;
    return client.portfolioFee.reduce(
      (acc, pf) => acc + (pf.portfolios || []).filter((p) => p && p.isSelected).length,
      0
    );
  };

  /** Number of visible and selectable (no default cash account issue) portfolios for this client. */
  getClientVisibleTotal = (client) => {
    if (!client || !client.portfolioFee) return 0;
    return client.portfolioFee.reduce(
      (acc, pf) =>
        acc +
        (pf.portfolios || []).filter(
          (p) => p && this.portfolioMatchesIncludedIssues(p) && this.portfolioIsSelectable(p)
        ).length,
      0
    );
  };

  /** Number of visible selectable portfolios that are selected for this client. */
  getClientVisibleSelectedCount = (client) => {
    if (!client || !client.portfolioFee) return 0;
    return client.portfolioFee.reduce(
      (acc, pf) =>
        acc +
        (pf.portfolios || []).filter(
          (p) =>
            p &&
            this.portfolioMatchesIncludedIssues(p) &&
            this.portfolioIsSelectable(p) &&
            p.isSelected
        ).length,
      0
    );
  };

  getPercentageFromDecimal = (value) => {
    return +(value * 100).toFixed(2);
  };

  /** Returns the cash account object in cashAccountBalances matching selectedCashAccountId (dropdown binds by id). */
  getSelectedCashAccount = (portfolio) => {
    if (!portfolio || !portfolio.cashAccountBalances || portfolio.selectedCashAccountId == null) return null;
    return portfolio.cashAccountBalances.find(
      (cb) => cb.cashItem && cb.cashItem.id === portfolio.selectedCashAccountId
    ) || null;
  };

  /** True when portfolio has fee parameters to display (avoids ugly rendering when fee calculation not run yet). */
  hasFeeParameters = (portfolio) => {
    return portfolio && portfolio.feeParameters && portfolio.feeParameters.length > 0;
  };

  getPortfolioFeeNav = (portfolio) => {
    if (!this.hasFeeParameters(portfolio) || !portfolio.defaultCurrency) return undefined;
    const fp = _.first(_.filter(portfolio.feeParameters, (p) => p.currency === portfolio.defaultCurrency));
    return fp ? fp.marketValue : undefined;
  };

  getPortfolioFeeNavDelta = (portfolio) => {
    if (!this.hasFeeParameters(portfolio) || !portfolio.defaultCurrency) return undefined;
    const fp = _.first(_.filter(portfolio.feeParameters, (p) => p.currency === portfolio.defaultCurrency));
    return fp ? fp.marketValueDelta : undefined;
  };

  getPortfolioFeeNavMUR = (portfolio) => {
    if (!this.hasFeeParameters(portfolio)) return undefined;
    const fp = _.first(_.filter(portfolio.feeParameters, (p) => p.currency === "MUR"));
    return fp ? fp.marketValue : undefined;
  };

  getPortfolioFeeExclusion = (portfolio) => {
    if (!this.hasFeeParameters(portfolio) || !portfolio.defaultCurrency) return undefined;
    const fp = _.first(_.filter(portfolio.feeParameters, (p) => p.currency === portfolio.defaultCurrency));
    return fp ? fp.exclusionValue : undefined;
  };

  getPortfolioFeeExclusionMUR = (portfolio) => {
    if (!this.hasFeeParameters(portfolio)) return undefined;
    const fp = _.first(_.filter(portfolio.feeParameters, (p) => p.currency === "MUR"));
    return fp ? fp.exclusionValue : undefined;
  };

  getPortfolioFeeValue = (portfolio) => {
    if (!this.hasFeeParameters(portfolio) || !portfolio.defaultCurrency) return undefined;
    const feeParam = _.first(
      _.filter(portfolio.feeParameters, (p) => p.currency === portfolio.defaultCurrency)
    );
    return feeParam ? feeParam.feeValue : 0;
  };

  getPortfolioFeeValueDelta = (portfolio) => {
    if (!this.hasFeeParameters(portfolio) || !portfolio.defaultCurrency) return undefined;
    const fp = _.first(_.filter(portfolio.feeParameters, (p) => p.currency === portfolio.defaultCurrency));
    return fp ? fp.feeValueDelta : undefined;
  };

  getPortfolioFeeValueMUR = (portfolio) => {
    if (!this.hasFeeParameters(portfolio)) return undefined;
    const feeParam = _.first(
      _.filter(portfolio.feeParameters, (p) => p.currency === "MUR")
    );
    return feeParam ? feeParam.feeValue : 0;
  };

  getPortfolioSetupFeeValue = (portfolio) => {
    if (!this.hasFeeParameters(portfolio) || !portfolio.defaultCurrency) return undefined;
    const feeParam = _.first(
      _.filter(portfolio.feeParameters, (p) => p.currency === portfolio.defaultCurrency)
    );
    return feeParam ? (feeParam.setupFeeValue || 0) : 0;
  };

  getPortfolioSetupFeeValueMUR = (portfolio) => {
    if (!this.hasFeeParameters(portfolio)) return undefined;
    const feeParam = _.first(
      _.filter(portfolio.feeParameters, (p) => p.currency === "MUR")
    );
    return feeParam ? (feeParam.setupFeeValue || 0) : 0;
  };

  hasAnySetupFees = (portfolios) => {
    if (!portfolios || portfolios.length === 0) {
      return false;
    }
    return portfolios.some((portfolio) => {
      if (!portfolio || !portfolio.feeParameters) {
        return false;
      }
      return portfolio.feeParameters.some((fp) => (fp.setupFeeValue || 0) > 0);
    });
  };

  displayFeeSummary = (client) => {
    client.displayFeeSummary = !client.displayFeeSummary;
  };

  /** Select/deselect only visible selectable portfolios for this client. */
  selectAllClientPortfolios = (client) => {
    client.portfolioFee.forEach((fee) => {
      (fee.portfolios || []).forEach((portfolio) => {
        if (portfolio && this.portfolioMatchesIncludedIssues(portfolio) && this.portfolioIsSelectable(portfolio)) {
          portfolio.isSelected = client.isSelected;
        }
      });
    });
  };

  stopPropagation = ($event: angular.IAngularEvent) => {
    $event.stopPropagation();
  };

  selectPortfolio = (portfolio, client) => {
    const visibleTotal = this.getClientVisibleTotal(client);
    const visibleSelected = this.getClientVisibleSelectedCount(client);
    client.isSelected = visibleTotal > 0 && visibleSelected === visibleTotal;
  };

  /** Text for client row: shows selected count and total portfolio count (total is always all portfolios, not filtered). */
  getPortfolioSelectedText = (client) => {
    if (!client) return "";
    const total = this.getClientTotalPortfolioCount(client);
    const selected = this.getClientSelectedCount(client);
    if (total === 0) return "0 of 0 portfolio(s) ";
    if (selected === total) return "All portfolio(s)";
    return `${selected} of ${total} portfolio(s) `;
  };

  updateFeeParameters = (portfolio) => {
    portfolio.selectedCashAccount = this.getSelectedCashAccount(portfolio);
    if (portfolio.selectedCashAccount) {
      portfolio.defaultCashAccount = portfolio.selectedCashAccount.cashItem.id;
      portfolio.defaultCurrency = portfolio.selectedCashAccount.currency;
    }
  };

  canRecalculateFees = () => {
    if (!this.clientFeeParameters) return true;
    const hasSelectedClients = this.clientFeeParameters.some((client) => client && client.isSelected);
    return !hasSelectedClients;
  };

  canSaveTempRun = () => {
    let totalSelectableSelected = 0;
    let anyNonSelectableSelected = false;
    if (this.clientFeeParameters) {
      this.clientFeeParameters.forEach((client) => {
        if (!client || !client.portfolioFee) return;
        client.portfolioFee.forEach((pf) => {
          (pf.portfolios || []).forEach((p) => {
            if (!p) return;
            if (p.isSelected && this.portfolioHasNoDefaultCashAccount(p)) {
              anyNonSelectableSelected = true;
            } else if (p.isSelected && this.portfolioIsSelectable(p)) {
              totalSelectableSelected += 1;
            }
          });
        });
      });
    }
    // Disable save when no selectable portfolios are selected, or when any selected portfolio has no default cash account
    return totalSelectableSelected <= 0 || anyNonSelectableSelected;
  };

  saveTempRun = () => {
    let feeCalculationType = this.displayMCB
      ? FeeCalculationType.MCB
      : FeeCalculationType.EDR;
    const tempRunParameters = [];

    this.clientFeeParameters.forEach((client) => {
      if (client) {
        client.portfolioFee.forEach((pf) => {
          pf.portfolios.forEach((p) => {
            if (p && p.isSelected && this.portfolioIsSelectable(p)) {
              const selectedAccount = this.getSelectedCashAccount(p);
              // Include all selected portfolios; when no cash account is selected, send null and backend will not save cash account details
              const cashAccountId = (selectedAccount && selectedAccount.cashItem && selectedAccount.cashItem.id != null)
                ? selectedAccount.cashItem.id
                : null;
              // When no cash account selected, use first fee parameter currency or MUR (no defaultCurrency)
              const currency = (selectedAccount && selectedAccount.currency) ||
                (p.feeParameters && p.feeParameters[0] && p.feeParameters[0].currency) || "MUR";

              const regularFeeValue = this.getPortfolioFeeValue(p) || 0;
              const setupFeeValue = this.getPortfolioSetupFeeValue(p) || 0;
              const regularFeeValueMUR = this.getPortfolioFeeValueMUR(p) || 0;
              const setupFeeValueMUR = this.getPortfolioSetupFeeValueMUR(p) || 0;

              const managementFeeParameterIds = (p.feeParameters || [])
                .map((fp) => fp.id)
                .filter((id) => id != null && id > 0);

              tempRunParameters.push({
                clientId: client.clientId != null ? client.clientId : client.id,
                portfolioId: p.portfolioId,
                portfolioFeeId: pf.portfolioFeeId,
                cashAccountId: cashAccountId,
                currency: currency,
                marketValue: this.getPortfolioFeeNav(p),
                marketValueMUR: this.getPortfolioFeeNavMUR(p),
                exclusionValue: this.getPortfolioFeeExclusion(p),
                exclusionValueMUR: this.getPortfolioFeeExclusionMUR(p),
                feeValue: regularFeeValue,
                feeValueMUR: regularFeeValueMUR,
                setupFeeValue: setupFeeValue,
                setupFeeValueMUR: setupFeeValueMUR,
                feeCalculationType: feeCalculationType,
                feeDate: client.feeDate,
                managementFeeParameterIds: managementFeeParameterIds,
              });
            }
          });
        });
      }
    });

    if (tempRunParameters.length === 0) {
      this.notifyjs("No portfolios selected for saving.");
      return;
    }

    this.isSavingParameters = true;
    this.feesRepo
      .saveTempRunParameters(tempRunParameters)
      .then((res) => {
        this.getNewInstructionsParameters();
        if (this.onSaveComplete) {
          this.onSaveComplete();
        }
      })
      .catch((err) => {
        this.notifyjs("Failed to save temp run " + extractErrorMessage(err));
      })
      .finally(() => (this.isSavingParameters = false));
  };

  formatDate(selectedDate) {
    return moment(selectedDate).endOf("month").format("YYYY-MM");
  }

  getMidRateForMonth = () => {
    if (!this.midRateDate) return;
    
    this.feesRepo
      .getMidRate(moment(this.midRateDate).endOf("month").format("YYYY-MM-DD"))
      .then((res) => {
        this.midRates = res.data as Array<any>;
      })
      .catch((err) => {
        this.notifyjs("Failed to get mid rates for selected month " + extractErrorMessage(err));
      });
  };

  setTotalNav = () => {
    this.totalNav = this.clientFeeParameters.reduce((acc, client) => {
      if (client) {
        return acc + client.totalNAV;
      }
      return acc;
    }, 0);
  };

  getSelectedNav = () => {
    let selectedNav = 0;
    if (this.clientFeeParameters.length > 0) {
      selectedNav = this.clientFeeParameters.reduce((a, client) => {
        if (client) {
          return (
            a +
            client.portfolioFee.reduce((acc, pf) => {
              return (
                acc +
                pf.portfolios
                  .filter((p) => p && p.isSelected && this.hasFeeParameters(p))
                  .reduce((a, p) => {
                    const murParam = p.feeParameters.filter((fp) => fp.currency == "MUR")[0];
                    return a + (murParam ? murParam.marketValue : 0);
                  }, 0)
              );
            }, 0)
          );
        }
        return a;
      }, 0);
    }
    return selectedNav;
  };

  setTotalFeeValue = () => {
    this.totalFeeValue = this.clientFeeParameters.reduce((acc, client) => {
      if (client) {
        return acc + client.totalFeeValue;
      }
      return acc;
    }, 0);
  };

  getSelectedFeeValue = () => {
    let selectedFeeValue = 0;
    if (this.clientFeeParameters.length > 0) {
      selectedFeeValue = this.clientFeeParameters.reduce((a, client) => {
        if (client) {
          return (
            a +
            client.portfolioFee.reduce((acc, pf) => {
              return (
                acc +
                pf.portfolios
                  .filter((p) => p && p.isSelected && this.hasFeeParameters(p))
                  .reduce((a, p) => {
                    const murParam = p.feeParameters.find((fp) => fp.currency == "MUR");
                    const regularFeeValue = murParam ? murParam.feeValue : 0;
                    const setupFeeValue = murParam ? (murParam.setupFeeValue || 0) : 0;
                    return a + regularFeeValue + setupFeeValue;
                  }, 0)
              );
            }, 0)
          );
        }
        return a;
      }, 0);
    }
    return selectedFeeValue;
  };

  setTotalPortfolios = () => {
    this.totalPortfolios = this.clientFeeParameters.reduce((a, client) => {
      if (client) {
        return (
          a +
          client.portfolioFee.reduce((acc, pf) => {
            return acc + pf.portfolios.filter((p) => p).length;
          }, 0)
        );
      }
      return a;
    }, 0);
  };

  getSelectedPortfolios = () => {
    let selectedPortfolios = 0;
    if (this.clientFeeParameters.length > 0) {
      selectedPortfolios = this.clientFeeParameters.reduce((a, client) => {
        if (client) {
          return (
            a +
            client.portfolioFee.reduce((acc, pf) => {
              return (
                acc + pf.portfolios.filter((p) => p && p.isSelected).length
              );
            }, 0)
          );
        }
        return a;
      }, 0);
    }
    return selectedPortfolios;
  };

  /** Number of selected portfolios that have at least one issue code. */
  getSelectedWithIssuesCount = (): number => {
    let count = 0;
    if (!this.clientFeeParameters || this.clientFeeParameters.length === 0) return 0;
    this.clientFeeParameters.forEach((client) => {
      if (!client || !client.portfolioFee) return;
      client.portfolioFee.forEach((pf) => {
        if (!pf || !pf.portfolios) return;
        pf.portfolios.forEach((p) => {
          if (p && p.isSelected && this.hasIssues(p)) count += 1;
        });
      });
    });
    return count;
  };

  hasIssues = (portfolio) => {
    return portfolio && portfolio.issueCodes && portfolio.issueCodes.length > 0;
  };

  /** True when portfolio has no linked default cash account; selection and save must be disabled until resolved. */
  portfolioHasNoDefaultCashAccount = (portfolio): boolean => {
    return !!(
      portfolio &&
      portfolio.issueCodes &&
      portfolio.issueCodes.length > 0 &&
      portfolio.issueCodes.includes("NO_DEFAULT_CASH_ACCOUNT")
    );
  };

  /**
   * True when the portfolio has no management fee parameters for this period in any state.
   * The calculation never ran or failed. The portfolio must be recalculated before it can be
   * saved to temp run. Selection for Save is disabled; the client row can still be selected
   * for Recalculate Fees.
   */
  portfolioIsNotCalculated = (portfolio): boolean => {
    return !!(
      portfolio &&
      portfolio.issueCodes &&
      portfolio.issueCodes.length > 0 &&
      portfolio.issueCodes.includes("NOT_CALCULATED")
    );
  };

  /** True when portfolio can be selected for Save Temp Run (has default cash account AND has been calculated). */
  portfolioIsSelectable = (portfolio): boolean => {
    return portfolio && !this.portfolioHasNoDefaultCashAccount(portfolio) && !this.portfolioIsNotCalculated(portfolio);
  };

  /** Set of issue codes that appear in at least one portfolio in the current data (used to disable filter options with no matches). */
  getIssueCodesPresentInData = (): Set<string> => {
    const present = new Set<string>();
    (this.clientFeeParameters || []).forEach((client) => {
      if (!client || !client.portfolioFee) return;
      client.portfolioFee.forEach((pf) => {
        if (!pf || !pf.portfolios) return;
        pf.portfolios.forEach((p) => {
          if (p && p.issueCodes && p.issueCodes.length > 0) {
            p.issueCodes.forEach((code) => {
              if (code) present.add(code);
            });
          }
        });
      });
    });
    return present;
  };

  /** True if at least one portfolio in the current data has this issue code (or "No Issues" has matching portfolios). */
  issueCodeHasMatchingPortfolios = (code: string): boolean => {
    if (!code) return false;
    if (code === TempRunController.NO_ISSUES_CODE) return this.getPortfoliosWithNoIssuesCount() > 0;
    return this.getIssueCodesPresentInData().has(code);
  };

  /** Issue code options that are present in the current data (for dropdown: only show these, do not show absent types). */
  getAvailableIssueCodeOptions = (): Array<{ code: string; label: string }> => {
    return (this.issueCodeOptions || []).filter((opt) => this.issueCodeHasMatchingPortfolios(opt.code));
  };

  /** Number of portfolios (rows) that have no issue codes. */
  getPortfoliosWithNoIssuesCount = (): number => {
    let count = 0;
    (this.clientFeeParameters || []).forEach((client) => {
      if (!client || !client.portfolioFee) return;
      client.portfolioFee.forEach((pf) => {
        if (!pf || !pf.portfolios) return;
        pf.portfolios.forEach((p) => {
          if (p && !this.hasIssues(p)) count += 1;
        });
      });
    });
    return count;
  };

  /** Total number of portfolios (rows) that have at least one issue code. */
  getPortfoliosWithIssuesCount = (): number => {
    let count = 0;
    (this.clientFeeParameters || []).forEach((client) => {
      if (!client || !client.portfolioFee) return;
      client.portfolioFee.forEach((pf) => {
        if (!pf || !pf.portfolios) return;
        pf.portfolios.forEach((p) => {
          if (p && this.hasIssues(p)) count += 1;
        });
      });
    });
    return count;
  };

  /** Number of portfolios (rows) that have this specific issue code (or "No Issues" = count with no issues). */
  getIssueCodeCount = (code: string): number => {
    if (!code) return 0;
    if (code === TempRunController.NO_ISSUES_CODE) return this.getPortfoliosWithNoIssuesCount();
    let count = 0;
    (this.clientFeeParameters || []).forEach((client) => {
      if (!client || !client.portfolioFee) return;
      client.portfolioFee.forEach((pf) => {
        if (!pf || !pf.portfolios) return;
        pf.portfolios.forEach((p) => {
          if (p && p.issueCodes && p.issueCodes.length > 0 && p.issueCodes.includes(code)) count += 1;
        });
      });
    });
    return count;
  };

  /** Remove from includedIssueCodes any code that has no matching portfolios so disabled options are not selected. */
  syncIncludedIssueCodesToAvailable = () => {
    const present = this.getIssueCodesPresentInData();
    if (!this.includedIssueCodes || this.includedIssueCodes.length === 0) return;
    this.includedIssueCodes = this.includedIssueCodes.filter((c) =>
      c === TempRunController.NO_ISSUES_CODE ? this.getPortfoliosWithNoIssuesCount() > 0 : present.has(c)
    );
  };

  /** Set default issue filter selection when data loads: all issue types that have matching portfolios are selected. */
  setDefaultIssueCodeSelection = () => {
    this.includedIssueCodes = this.issueCodeOptions
      .filter((opt) => this.issueCodeHasMatchingPortfolios(opt.code))
      .map((opt) => opt.code);
  };

  /** True if portfolio should be shown by the issue filter. Show if: (no issues and "No Issues" selected) OR (has at least one selected issue code). Nothing selected = show nothing. */
  portfolioMatchesIncludedIssues = (portfolio) => {
    const included = this.includedIssueCodes || [];
    const hasIssues = portfolio && portfolio.issueCodes && portfolio.issueCodes.length > 0;
    if (included.length === 0) return false;
    if (!hasIssues) return included.includes(TempRunController.NO_ISSUES_CODE);
    return portfolio.issueCodes.some((code) => included.includes(code));
  };

  /** True if this fee structure has at least one portfolio that passes the issue filter (so we show the fee section). */
  portfolioFeeHasVisiblePortfolios = (portfolioFee) => {
    if (!portfolioFee || !portfolioFee.portfolios || !portfolioFee.portfolios.length) {
      return false;
    }
    return portfolioFee.portfolios.some((p) => p && this.portfolioMatchesIncludedIssues(p));
  };

  /** Returns a predicate for use with Angular filter: show client if it matches search and has at least one portfolio matching the issue filter. */
  getSearchAndIssueFilter = () => {
    const searchText = (this.searchText || "").trim().toLowerCase();
    return (client: any) => {
      if (!client) return false;
      const hasVisiblePortfolio =
        client.portfolioFee &&
        client.portfolioFee.some((pf) =>
          pf.portfolios &&
          pf.portfolios.some((p) => p && this.portfolioMatchesIncludedIssues(p))
        );
      if (!hasVisiblePortfolio) return false;
      if (!searchText) return true;
      const name = (client.clientName || "").toLowerCase();
      const number = (client.clientNumber || "").toLowerCase();
      let portfolioMatch = false;
      if (client.portfolioFee) {
        client.portfolioFee.forEach((pf) => {
          if (pf.portfolios) {
            pf.portfolios.forEach((p) => {
              if (p && (p.portfolioName || "").toLowerCase().indexOf(searchText) >= 0) {
                portfolioMatch = true;
              }
            });
          }
        });
      }
      return name.indexOf(searchText) >= 0 || number.indexOf(searchText) >= 0 || portfolioMatch;
    };
  };

  getIssueCodesText = (portfolio) => {
    if (portfolio && portfolio.issueCodes && portfolio.issueCodes.length > 0) {
      return portfolio.issueCodes.join(", ");
    }
    return "";
  };

  getClientIssueCodesText = (client) => {
    if (!client) {
      return "";
    }
    
    const allIssueCodes = new Set<string>();
    
    if (client.portfolioFee) {
      client.portfolioFee.forEach((pf) => {
        if (pf.portfolios) {
          pf.portfolios.forEach((p) => {
            if (p && p.issueCodes && p.issueCodes.length > 0) {
              p.issueCodes.forEach((code) => {
                if (code) {
                  allIssueCodes.add(code);
                }
              });
            }
          });
        }
      });
    }
    
    return Array.from(allIssueCodes).join(", ");
  };

  clientHasIssues = (client) => {
    if (client) {
      return client.portfolioFee.some((pf) => {
        return pf.portfolios.some((p) => {
          return p && this.hasIssues(p);
        });
      });
    }
  };

  /** True if client has at least one visible (non-excluded) portfolio with issues. */
  clientHasVisibleIssues = (client) => {
    if (!client || !client.portfolioFee) return false;
    return client.portfolioFee.some((pf) =>
      pf.portfolios &&
      pf.portfolios.some(
        (p) => p && this.portfolioMatchesIncludedIssues(p) && this.hasIssues(p)
      )
    );
  };

  /** Issue codes text for client row: only from visible (non-excluded) portfolios. */
  getClientVisibleIssueCodesText = (client) => {
    if (!client) return "";
    const allIssueCodes = new Set<string>();
    if (client.portfolioFee) {
      client.portfolioFee.forEach((pf) => {
        if (pf.portfolios) {
          pf.portfolios.forEach((p) => {
            if (p && this.portfolioMatchesIncludedIssues(p) && p.issueCodes && p.issueCodes.length > 0) {
              p.issueCodes.forEach((code) => {
                if (code) allIssueCodes.add(code);
              });
            }
          });
        }
      });
    }
    return Array.from(allIssueCodes).join(", ");
  };

  generateAdhocFeeInstructionSheet = () => {
    let feeCalculationType = this.displayMCB
      ? FeeCalculationType.MCB
      : FeeCalculationType.EDR;
    this.isGeneratingExcel = true;
    let fromDate = moment(this.fromDate).endOf("month").format("YYYY-MM-DD");
    let toDate = moment(this.toDate).endOf("month").format("YYYY-MM-DD");

    this.feesRepo
      .generateAdhocTempRunSheetFromTempRunFees(fromDate, toDate, feeCalculationType)
      .then((res) => {
        let instructionExcel = res.data as any;
        this.$window.open(instructionExcel.url, "_blank");
      })
      .catch((err) => {
        this.notifyjs("Failed to generate ad-hoc instruction");
      })
      .finally(() => (this.isGeneratingExcel = false));
  };

  recalculateFees = () => {
    const recalculateFees = this.clientFeeParameters
      .filter((client) => client.isSelected)
      .map((client) => ({
        clientNumber: client.clientNumber,
        calculationDate: client.feeDate,
      }));
    if (recalculateFees.length === 0) {
      this.notifyjs("Select at least one client to recalculate", "warning");
      return;
    }
    this.isRecalculating = true;
    this.feesRepo
      .recalculateFees(recalculateFees)
      .then((res) => {
        const data = (res.data || {}) as {
          updated?: number;
          failures?: Array<{ clientNumber: string; feeDate: string; reason: string }>;
        };
        const updated = data.updated !== undefined ? data.updated : 0;
        const failures = data.failures || [];
        this.getNewInstructionsParameters();
        if (failures.length > 0) {
          const failureLines = failures
            .map((f) => `${f.clientNumber} (${f.feeDate}): ${f.reason}`)
            .join("\n");
          this.notifyjs(
            `Recalculate complete: ${updated} updated, ${failures.length} failed\n\n${failureLines}`,
            "warning"
          );
        } else {
          this.notifyjs(`Recalculate complete: ${updated} client-month(s) updated`, "success");
        }
      })
      .catch((err) => {
        this.notifyjs(extractErrorMessage(err, "Failed to recalculate fees"));
      })
      .finally(() => (this.isRecalculating = false));
  };
}

export const tempRun: angular.IComponentOptions = {
  template: require("./fees.instructions.temprun.html"),
  controller: TempRunController,
  bindings: {
    reloadTempParameters: "<",
    fromDate: "<",
    toDate: "<",
    displayMCB: "<",
    threshold: "<",
    midRateDate: "<",
    midRates: "<",
    currentDate: "<",
    lastTwelveMonths: "<",
    onSaveComplete: "&",
  },
};



// WEBPACK FOOTER //
// ./src/app/fees/instructions/temprun/fees.instructions.temprun.ts